"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";
import { Input } from "./input";
import { Search, X } from "lucide-react";

export interface SearchBoxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  /** Controlled search value */
  value?: string;
  /** Default uncontrolled value */
  defaultValue?: string;
  /** Change handler with string value */
  onChange?: (value: string) => void;
  /** Callback when search is submitted (Enter key) */
  onSearch?: (value: string) => void;
  /** Callback when clear button is clicked */
  onClear?: () => void;
  /** Show clear button when value exists */
  showClear?: boolean;
  /** Custom search icon */
  searchIcon?: React.ReactNode;
  /** Custom clear icon */
  clearIcon?: React.ReactNode;
  /** Root container class */
  containerClassName?: string;
}

/**
 * SearchBox is a styled search input with icon and optional clear button.
 * Supports controlled and uncontrolled modes.
 */
export const SearchBox = React.forwardRef<HTMLInputElement, SearchBoxProps>(
  (
    {
      className,
      containerClassName,
      value: valueProp,
      defaultValue = "",
      onChange,
      onSearch,
      onClear,
      showClear = true,
      searchIcon,
      clearIcon,
      placeholder = "Search...",
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const isControlled = valueProp !== undefined;
    const value = isControlled ? valueProp : internalValue;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    };

    const handleClear = () => {
      if (!isControlled) {
        setInternalValue("");
      }
      onChange?.("");
      onClear?.();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && onSearch) {
        e.preventDefault();
        onSearch(value);
      }
    };

    const showClearButton = showClear && value.length > 0;

    return (
      <div className={cn(styleDrivers.searchBox.root, containerClassName)}>
        <div className={styleDrivers.searchBox.inputWrapper}>
          <div className={styleDrivers.searchBox.icon}>
            {searchIcon ?? <Search />}
          </div>
          <Input
            ref={ref}
            type="search"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(styleDrivers.searchBox.input, className)}
            style={{ paddingLeft: "2.5rem", paddingRight: "2rem" }}
            {...props}
          />
          {showClearButton && (
            <button
              type="button"
              onClick={handleClear}
              className={styleDrivers.searchBox.clearButton}
              aria-label="Clear search"
            >
              {clearIcon ?? <X />}
            </button>
          )}
        </div>
      </div>
    );
  }
);

SearchBox.displayName = "SearchBox";

export default SearchBox;
